import { useEffect, useRef, useState } from "react";

export interface SiteSubscriptionStatus {
    bloqueada: boolean;
}

export interface BlockedStoreContact {
    whatsappNumber: string | null;
    whatsappMessage: string;
    whatsappUrl: string | null;
}

export interface BlockedStoreContactData {
    whatsapp?: string | null;
    whatsappMessage?: string | null;
}

export interface BlockedStoreStoreInfo {
    whatsappHabilitado?: boolean | null;
    whatsappNumero?: string | null;
}

export interface UseSiteSubscriptionGateOptions<TStoreInfo extends BlockedStoreStoreInfo> {
    getStatus: () => Promise<SiteSubscriptionStatus>;
    getStoreInfo?: () => Promise<TStoreInfo>;
    contactData?: BlockedStoreContactData | null;
}

export interface UseSiteSubscriptionGateResult {
    isStoreGateLoading: boolean;
    isStoreBlocked: boolean;
    blockedStoreContact: BlockedStoreContact | null;
}

const DEFAULT_WHATSAPP_MESSAGE = "Hola, quiero mas informacion sobre el estado de la tienda.";

const getDigitsOnly = (value?: string | null): string => (value ?? "").replace(/\D/g, "");

const buildWhatsappUrl = (whatsappNumber: string, whatsappMessage: string): string =>
    `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMessage)}`;

export const resolveBlockedStoreContact = <TStoreInfo extends BlockedStoreStoreInfo>(
    storeInfo: TStoreInfo | null | undefined,
    contactData?: BlockedStoreContactData | null,
): BlockedStoreContact | null => {
    const whatsappMessage = contactData?.whatsappMessage || DEFAULT_WHATSAPP_MESSAGE;

    const whatsappStoreInfo =
        storeInfo?.whatsappHabilitado === true ? getDigitsOnly(storeInfo.whatsappNumero) : "";

    const whatsappFallback = getDigitsOnly(contactData?.whatsapp);
    const whatsappNumber = whatsappStoreInfo || whatsappFallback;

    if (!whatsappNumber) {
        return null;
    }

    return {
        whatsappNumber,
        whatsappMessage,
        whatsappUrl: buildWhatsappUrl(whatsappNumber, whatsappMessage),
    };
};

export const useSiteSubscriptionGate = <TStoreInfo extends BlockedStoreStoreInfo>(
    options: UseSiteSubscriptionGateOptions<TStoreInfo>,
): UseSiteSubscriptionGateResult => {
    const optionsRef = useRef(options);
    const [isStoreGateLoading, setIsStoreGateLoading] = useState(true);
    const [isStoreBlocked, setIsStoreBlocked] = useState(false);
    const [blockedStoreContact, setBlockedStoreContact] = useState<BlockedStoreContact | null>(
        null,
    );

    optionsRef.current = options;

    useEffect(() => {
        let bitCancelado = false;

        const cargarEstadoSitioWeb = async () => {
            const { getStatus, getStoreInfo, contactData } = optionsRef.current;

            try {
                const estado = await getStatus();
                if (bitCancelado) return;

                if (!estado.bloqueada) {
                    setIsStoreBlocked(false);
                    setBlockedStoreContact(null);
                    return;
                }

                setIsStoreBlocked(true);

                if (!getStoreInfo) {
                    setBlockedStoreContact(
                        resolveBlockedStoreContact<TStoreInfo>(null, contactData),
                    );
                    return;
                }

                try {
                    const tiendaInfo = await getStoreInfo();
                    if (bitCancelado) return;

                    setBlockedStoreContact(resolveBlockedStoreContact(tiendaInfo, contactData));
                } catch {
                    if (bitCancelado) return;
                    setBlockedStoreContact(
                        resolveBlockedStoreContact<TStoreInfo>(null, contactData),
                    );
                }
            } catch {
                if (bitCancelado) return;

                // Fail-open para evitar bajar la tienda por un error transitorio del endpoint.
                setIsStoreBlocked(false);
                setBlockedStoreContact(null);
            } finally {
                if (!bitCancelado) {
                    setIsStoreGateLoading(false);
                }
            }
        };

        cargarEstadoSitioWeb();

        return () => {
            bitCancelado = true;
        };
    }, []);

    return {
        isStoreGateLoading,
        isStoreBlocked,
        blockedStoreContact,
    };
};
